import { Divider, Icon, Menu, MenuItem } from "@mui/material";
import MDTypography from "components/Basics/MDTypography";
import ConfirmDialogButton from "components/Custom/Dialogs/ConfirmDialogButton";
import { useFormActions } from "hooks/useFormActions";
import { useDispatch } from "react-redux";
import { display } from "redux-react/reducers/snackBarReducer";
import { v4 } from "uuid";

const MenuCategory = ({
	anchorEl,
	onClose,
	menuRef,
	folder,
	onAddFile,
	handleEditAsset,
	onDeleteFolder,
	t,
	themeActions
}) => {
	const dispatch = useDispatch();
	const { startCategoryTraining, startCategoryUntraining, translateElements } = themeActions;
	const { getItemsFromCollection } = useFormActions();

	/**
	 * Get answer items by type and parent codes
	 * @param {string} type
	 * @param {array} parentCodes
	 * @returns {Promise<Array>}
	 */
	const getAnswerItems = async (type, parentCodes) => {
		return getItemsFromCollection("answerItem", {
			query: {
				documentType: "answerType",
				type,
				parent: {
					$in: parentCodes
				}
			},
			projection: {
				_id: 0,
				code: 1
			}
		});
	};

	/**
	 * Translate all categories names from a theme
	 */
	const translateCategoryName = async () => {
		const TYPE = "themeCategory";
		translateElements([folder.code], TYPE);
		onClose();
	};

	/**
	 * Translate all questions from a theme
	 */
	const translateQuestions = async () => {
		const TYPE = "themeQuestion";
		const questions = await getAnswerItems(TYPE, [folder.code]);
		const questionsCodes = questions.map(row => row.code);
		translateElements(questionsCodes, "themeCategory");
		onClose();
	};

	return (
		<Menu open={Boolean(anchorEl)} onClose={onClose} anchorEl={anchorEl} action={menuRef}>
			{/* Category */}
			<MenuItem disabled style={{ opacity: 1 }}>
				<MDTypography variant="caption">Categorie</MDTypography>
			</MenuItem>
			<MenuItem onClick={translateCategoryName}>
				<Icon sx={{ mr: 1 }} fontSize="medium">
					translate
				</Icon>
				Traduire le nom de la catégorie
			</MenuItem>
			<MenuItem onClick={() => handleEditAsset(folder)}>
				<Icon sx={{ mr: 1 }} fontSize="medium">
					edit
				</Icon>
				{t("SETTINGS.edit")}
			</MenuItem>

			{/* Delete category */}
			<ConfirmDialogButton
				onCancel={onClose}
				onConfirm={(e, values) => {
					onClose();
					onDeleteFolder(folder);
				}}
				component={
					<MenuItem
						style={{
							color: "red"
						}}
					>
						<Icon sx={{ mr: 1 }} fontSize="medium">
							delete
						</Icon>
						{t("SETTINGS.delete")}
					</MenuItem>
				}
				title={t("ASSETS.DELETE_CONFIRMATION.title")}
				content={t("ASSETS.THEME.confirmDeleteCategory", {
					name: folder.name
				})}
			/>

			<Divider />

			{/* Content */}
			<MenuItem disabled style={{ opacity: 1 }}>
				<MDTypography variant="caption">Questions / Réponses</MDTypography>
			</MenuItem>

			<MenuItem onClick={() => onAddFile(folder)}>
				<Icon sx={{ mr: 1 }} fontSize="medium">
					add
				</Icon>
				Nouvelle Q/R
			</MenuItem>

			{/* Translate all content from theme */}
			<MenuItem onClick={translateQuestions}>
				<Icon sx={{ mr: 1 }} fontSize="medium">
					translate
				</Icon>
				Traduire toutes les Q/R
			</MenuItem>

			{/* Train category dialog */}
			<ConfirmDialogButton
				onCancel={onClose}
				onConfirm={(e, values) => {
					onClose();

					const id = v4();

					// Display a message to the user
					dispatch(
						display({
							id,
							message: t("ASSETS.TRAIN.starting"),
							type: "loading"
						})
					);

					startCategoryTraining([folder.code], "category", id);
				}}
				component={
					<MenuItem>
						<Icon fontSize="medium">model_training</Icon>&nbsp;Entrainer toutes les Q/R
					</MenuItem>
				}
				title={t("ASSETS.THEME.startTrainingCategoryTitle")}
				content={t("ASSETS.THEME.startTrainingCategoryDescription")}
			/>

			{/* Untrain category dialog */}
			<ConfirmDialogButton
				onCancel={onClose}
				onConfirm={(e, values) => {
					onClose();
					dispatch(
						display({
							message: t("ASSETS.THEME.questionsUntrainingStarted"),
							type: "info"
						})
					);

					startCategoryUntraining(
						[folder.code],
						"category",
						() => {
							dispatch(
								display({
									message: t("ASSETS.THEME.questionsUntrainingFinish"),
									type: "success"
								})
							);
						},
						() => {
							dispatch(
								display({
									message: t("ASSETS.THEME.untrainingError"),
									type: "error"
								})
							);
						}
					);
				}}
				component={
					<MenuItem style={{ color: "red" }}>
						<Icon fontSize="medium">model_training</Icon>&nbsp;Désentrainer toutes les Q/R
					</MenuItem>
				}
				title={t("ASSETS.THEME.startUntrainingCategoryTitle")}
				content={t("ASSETS.THEME.startUntrainingCategoryDescription")}
			/>
		</Menu>
	);
};

export default MenuCategory;
